import { ResultFeedbackInputType, type ResultTemplateFeedback } from '@/types/composition-functions';

/**
 * Report-card traits as ratings (#574 phase 4): the pure logic shared by the
 * template builder, the result-entry templates and the read-only result views.
 * Mirrors the backend's FeedbackRating rules (2–10 scale, default 5 with
 * Poor..Excellent labels, one label per level or none).
 */

export const RATING_MIN_SCALE = 2;
export const RATING_MAX_SCALE = 10;
export const RATING_DEFAULT_SCALE = 5;

/** Lowest level first: 1 = Poor, 5 = Excellent. */
export const DEFAULT_RATING_LABELS_5: readonly string[] = ['Poor', 'Fair', 'Good', 'Very Good', 'Excellent'];

export const FEEDBACK_INPUT_TYPE_OPTIONS: ReadonlyArray<{ label: string; value: ResultFeedbackInputType }> = [
  { label: 'Written comment', value: ResultFeedbackInputType.Text },
  { label: 'Rating', value: ResultFeedbackInputType.Rating },
];

type RatingDefinition = Partial<Pick<ResultTemplateFeedback, 'input_type' | 'rating_max' | 'rating_scale_labels'>>;

export function isRatingItem(definition: RatingDefinition | null | undefined): boolean {
  return definition?.input_type === ResultFeedbackInputType.Rating;
}

/** Default labels for a scale, lowest first. */
export function defaultRatingLabels(max: number): string[] {
  return max === RATING_DEFAULT_SCALE
    ? [...DEFAULT_RATING_LABELS_5]
    : Array.from({ length: max }, (_, i) => String(i + 1));
}

/** Choices for the rating control: one per level, labelled by the school's wording. */
export function ratingOptions(definition: RatingDefinition): Array<{ label: string; value: number }> {
  const max = definition.rating_max || RATING_DEFAULT_SCALE;
  const labels = definition.rating_scale_labels?.length === max
    ? definition.rating_scale_labels
    : defaultRatingLabels(max);
  return labels.map((label, i) => ({ label, value: i + 1 }));
}

/** "Very Good (4/5)", or "4/5" when the label is just the number; "" when unset. */
export function formatRating(label: string | null | undefined, rating: number | null | undefined, max: number | null | undefined): string {
  if (rating === null || rating === undefined || !max) {
    return '';
  }
  const score = `${rating}/${max}`;
  return label && label !== String(rating) ? `${label} (${score})` : score;
}

/**
 * Input for saveStudentResultFeedback. A rating item sends only the rating so
 * the backend keeps any existing note; a text item sends the comment as before.
 */
export function buildFeedbackSaveFields(
  definition: RatingDefinition,
  comment: string | null | undefined,
  rating: number | null | undefined,
): { comment?: string | null; rating?: number | null } {
  return isRatingItem(definition)
    ? { rating: rating ?? null }
    : { comment: comment ?? '' };
}

// ── Template builder ────────────────────────────────────────────────────────

// @allow-handwritten editable builder dialog state; feedbackDefinitionPayload maps it to CreateResultTemplateFeedbackInput
export interface FeedbackFormModel {
  label: string;
  code: string;
  category: string;
  input_type: ResultFeedbackInputType;
  rating_max: number;
  rating_labels: string[];
}

export function emptyFeedbackForm(): FeedbackFormModel {
  return {
    label: '',
    code: '',
    category: '',
    input_type: ResultFeedbackInputType.Text,
    rating_max: RATING_DEFAULT_SCALE,
    rating_labels: defaultRatingLabels(RATING_DEFAULT_SCALE),
  };
}

/** Resize the label list to a new scale, keeping what the admin already typed. */
export function resizeRatingLabels(labels: string[], max: number): string[] {
  if (labels.length === max) return labels;
  const defaults = defaultRatingLabels(max);
  const untouched = labels.join('|') === defaultRatingLabels(labels.length).join('|');
  if (untouched) return defaults;
  return defaults.map((fallback, i) => labels[i] ?? fallback);
}

/** First problem with the dialog's rating settings, or null when they are valid. */
export function validateFeedbackForm(form: FeedbackFormModel): string | null {
  if (!form.label.trim()) {
    return 'Label is required.';
  }
  if (form.input_type !== ResultFeedbackInputType.Rating) {
    return null;
  }
  if (!Number.isInteger(form.rating_max) || form.rating_max < RATING_MIN_SCALE || form.rating_max > RATING_MAX_SCALE) {
    return `The rating scale must be between ${RATING_MIN_SCALE} and ${RATING_MAX_SCALE}.`;
  }
  const trimmed = form.rating_labels.map(l => l.trim());
  const anyFilled = trimmed.some(l => l !== '');
  if (anyFilled && (trimmed.length !== form.rating_max || trimmed.some(l => l === ''))) {
    return `Give a label for each of the ${form.rating_max} levels, or leave them all blank.`;
  }
  return null;
}

// @allow-handwritten builder list row (local draft of a feedback item, holds id/tempId alongside rating settings)
export interface FeedbackDraft {
  id?: string;
  label: string;
  code?: string | null;
  category?: string | null;
  input_type?: ResultFeedbackInputType | null;
  rating_max?: number | null;
  rating_labels?: string[] | null;
}

/** One feedback item for create/updateResultTemplate. */
export function feedbackDefinitionPayload(draft: FeedbackDraft, sequence: number) {
  const isRating = draft.input_type === ResultFeedbackInputType.Rating;
  const labels = (draft.rating_labels ?? []).map(l => l.trim());
  return {
    id: draft.id,
    label: draft.label,
    code: draft.code,
    category: draft.category || null,
    sequence,
    input_type: isRating ? ResultFeedbackInputType.Rating : ResultFeedbackInputType.Text,
    rating_max: isRating ? (draft.rating_max ?? RATING_DEFAULT_SCALE) : null,
    rating_labels: isRating && labels.some(l => l !== '') ? labels : null,
  };
}

/** Short description for the builder list, e.g. "Rating 1–5". */
export function feedbackTypeSummary(draft: FeedbackDraft): string {
  return draft.input_type === ResultFeedbackInputType.Rating
    ? `Rating 1–${draft.rating_max ?? RATING_DEFAULT_SCALE}`
    : 'Written comment';
}

/** Read-only text for a saved feedback: the rating (plus any note) for a rating item, else the comment. */
export function feedbackDisplayText(
  definition: RatingDefinition | null | undefined,
  saved: { comment?: string | null; rating?: number | null; rating_label?: string | null },
): string {
  if (isRatingItem(definition)) {
    const rating = formatRating(saved.rating_label, saved.rating, definition?.rating_max);
    const parts = [rating, saved.comment].filter(Boolean);
    return parts.length ? parts.join(' — ') : '-';
  }
  return saved.comment || '-';
}
